'use client'

import { Title, Text, Stack, Card, Grid, GridCol, ThemeIcon, Center } from '@mantine/core'
import Section from '@/components/ui/Section'
import { 
  IconSparkles,
  IconVideo,
  IconFileText
} from '@tabler/icons-react'

interface AIFeaturesSectionProps {
  variant?: 'mentor' | 'mentee'
}

export default function AIFeaturesSection({ variant = 'mentee' }: AIFeaturesSectionProps) {
  const isMentorView = variant === 'mentor'
  
  return (
    <Section bg="white" py="xl" contentWidth="lg">
      <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}>
        <Grid gutter="xl">
          <GridCol span={{ base: 12, md: 4 }}>
            <Stack gap="md">
              <Title order={2} size="h1" className="gradient-emerald">
                {isMentorView ? (
                  <>Tools that save&nbsp;you&nbsp;time.</>
                ) : (
                  'Everything You Need in One Place'
                )}
              </Title>
              <Text size="lg" c="gray.7" style={{ lineHeight: 1.7 }}>
                {isMentorView 
                  ? 'Stop taking notes and tracking tasks manually. MentorSub handles the details so you can focus on the conversation.'
                  : 'Get more from your mentorship with tools that track your progress, surface insights, and keep you accountable.'}
              </Text>
            </Stack>
          </GridCol>

          <GridCol span={{ base: 12, md: 8 }}>
            <Stack gap="xl">
            <div style={{
              display: 'flex',
              gap: '1.5rem',
              alignItems: 'flex-start',
            }}>
              <Center
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: '12px',
                  backgroundColor: 'var(--mantine-color-emerald-0)',
                  flexShrink: 0,
                }}
              >
                <IconSparkles size={28} color="var(--mantine-color-emerald-6)" stroke={2} />
              </Center>

              <Stack gap="xs" style={{ flex: 1 }}>
                <Title order={3} c="navy.8">
                  Smart Automation
                </Title>

                <Text size="md" c="gray.7" style={{ lineHeight: 1.7 }}>
                  {isMentorView
                    ? 'Conversations organized into projects, action items tracked automatically, smart reminders when to follow up, and suggestions on what to discuss next. Focus on mentoring, not admin.'
                    : 'Everything tracked automatically - projects, goals, action items, and reminders. Get conversation suggestions based on your progress. Zero manual work.'}
                </Text>
              </Stack>
            </div>

            <div style={{
              display: 'flex',
              gap: '1.5rem',
              alignItems: 'flex-start',
            }}>
              <Center
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: '12px',
                  backgroundColor: 'var(--mantine-color-emerald-0)',
                  flexShrink: 0,
                }}
              >
                <IconVideo size={28} color="var(--mantine-color-emerald-6)" stroke={2} />
              </Center>

              <Stack gap="xs" style={{ flex: 1 }}>
                <Title order={3} c="navy.8">
                  Video Meetings with Transcripts
                </Title>

                <Text size="md" c="gray.7" style={{ lineHeight: 1.7 }}>
                  {isMentorView
                    ? 'Meet right in the platform with automatic transcriptions. Review what was discussed anytime without taking notes during the call.'
                    : 'Built-in video calls with automatic transcripts. Review past meetings and reference key moments anytime.'}
                </Text>
              </Stack>
            </div>

            <div style={{
              display: 'flex',
              gap: '1.5rem',
              alignItems: 'flex-start',
            }}>
              <Center
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: '12px',
                  backgroundColor: 'var(--mantine-color-emerald-0)',
                  flexShrink: 0,
                }}
              >
                <IconFileText size={28} color="var(--mantine-color-emerald-6)" stroke={2} />
              </Center>

              <Stack gap="xs" style={{ flex: 1 }}>
                <Title order={3} c="navy.8">
                  Complete Context
                </Title>

                <Text size="md" c="gray.7" style={{ lineHeight: 1.7 }}>
                  {isMentorView
                    ? 'All conversations and meetings in one place. Jump back into any discussion with full context - never scramble to remember details.'
                    : 'Your entire mentorship journey organized and searchable. See all discussions, meetings, and progress in one place.'}
                </Text>
              </Stack>
            </div>
          </Stack>
        </GridCol>
      </Grid>
      </div>
    </Section>
  )
}
